// VOY-466 — "Match my taste" toggle for hotel search results.
//
// Auth-aware behaviour:
//   * Signed-in user → real toggle. Flips `personalizeEnabled` and
//     calls `onChange` so the parent can re-fetch with
//     `?personalize=true/false`. Default ON.
//   * Anonymous user → toggle is shown, NOT disabled, but the
//     click opens the sign-in flow (`/auth/login`). Conversion
//     surface: "personalised results" is a real reason for someone
//     to sign up. NOTE: post-login return-to-search is NOT yet wired
//     — see the TODO in `handleSignInClick`. A follow-up will restore
//     the search context once the auth callback gains a same-origin
//     allowlist.
//
// The toggle's visual treatment lives next to the sort/filter row
// in HotelTabContent. CSS lives in the global v6 stylesheet (utility
// classes only — no new file).

import { useCallback, useId } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles } from 'lucide-react'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

export interface MatchMyTasteToggleProps {
  /** Current toggle state. Ignored for anon users. */
  enabled: boolean
  /** Called when the user (signed-in only) flips the toggle. */
  onChange: (next: boolean) => void
  /** Whether the caller is authenticated. */
  authed: boolean
  /** Optional extra className for layout integration. */
  className?: string
}

export function MatchMyTasteToggle({ enabled, onChange, authed, className }: MatchMyTasteToggleProps) {
  const navigate = useNavigate()
  // Hoisted above the conditional return so the hook order stays
  // identical across renders (rules-of-hooks). The anon branch
  // doesn't render an input — the id is harmless there.
  const inputId = useId()

  const handleSignInClick = useCallback(() => {
    // TODO(VOY-466 follow-up): post-login return-to-page.
    // We initially wrote `voy.postSignInReturnTo` to sessionStorage AND
    // appended `?returnTo=…` to the login URL, but the audit found NO
    // consumer of either in the auth flow today — so neither did
    // anything, and the query param doubled as an open-redirect
    // surface waiting to misbehave once a consumer landed. Stripped
    // both. A future ticket should add the consumer (with a strict
    // same-origin allowlist) and then re-introduce the producer here.
    navigate('/auth/login')
  }, [navigate])

  if (!authed) {
    // Anon presentation — non-functional toggle with a clear CTA.
    // Click anywhere on the row opens sign-in.
    return (
      <button
        type="button"
        onClick={handleSignInClick}
        className={cn(
          'mmt-toggle mmt-toggle--anon',
          'inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm',
          'border-emerald-300 bg-emerald-50/50 text-emerald-900',
          'hover:bg-emerald-100 transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500',
          className,
        )}
        aria-label="Sign in to personalise hotel results to your taste"
      >
        <Sparkles className="h-4 w-4" aria-hidden="true" />
        <span className="font-medium">Match my taste</span>
        <span className="text-xs opacity-75">· Sign in to personalise</span>
      </button>
    )
  }

  // Authed presentation — real toggle. `inputId` (hoisted) is a
  // stable per-instance id so multiple toggles on the same page
  // (e.g. sticky header + sidebar) don't collide on `htmlFor`.
  return (
    <label
      htmlFor={inputId}
      className={cn(
        'mmt-toggle inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm cursor-pointer select-none',
        enabled
          ? 'border-emerald-400 bg-emerald-50 text-emerald-900'
          : 'border-neutral-300 bg-white text-neutral-700 hover:bg-neutral-50',
        'transition-colors',
        className,
      )}
    >
      <Sparkles
        className={cn('h-4 w-4', enabled ? 'text-emerald-600' : 'text-neutral-500')}
        aria-hidden="true"
      />
      <span className="font-medium">Match my taste</span>
      <Switch
        id={inputId}
        checked={enabled}
        onCheckedChange={onChange}
        aria-label="Personalise hotel results to your taste"
        className="ml-1"
      />
    </label>
  )
}
